import React from 'react';
import { cn } from '@/lib/utils';
import { 
  CheckCircle, 
  AlertCircle, 
  XCircle, 
  Clock, 
  ChevronRight,
  Loader2 
} from 'lucide-react';

/**
 * iOS 26-Inspired UI Components
 * =============================
 * Clean, spacious, frosted glass design
 */

// ============================================
// GLASS CARD - USES SEMANTIC TOKENS
// ============================================

export const GlassCard = React.forwardRef(({ 
  className, 
  variant = 'default',
  padding = 'default',
  children, 
  ...props 
}, ref) => {
  // Use inline styles for dark mode compatibility
  const getVariantStyles = () => {
    switch (variant) {
      case 'elevated':
        return {
          backgroundColor: 'hsl(var(--color-surface))',
          border: '1px solid hsl(var(--color-border))',
          boxShadow: 'var(--ios-shadow-lg)'
        };
      case 'subtle':
        return {
          backgroundColor: 'hsl(var(--color-surface) / 0.6)',
          backdropFilter: 'blur(12px)',
          border: '1px solid hsl(var(--color-border-subtle))',
          boxShadow: 'var(--ios-shadow-md)'
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          border: '1px solid hsl(var(--color-border))'
        };
      default:
        return {
          backgroundColor: 'hsl(var(--color-surface) / 0.8)',
          backdropFilter: 'blur(20px)',
          border: '1px solid hsl(var(--color-border))',
          boxShadow: 'var(--ios-shadow-lg)'
        };
    }
  };
  
  const paddings = {
    none: 'p-0',
    sm: 'p-4',
    default: 'p-6',
    lg: 'p-8'
  };
  
  return (
    <div
      ref={ref}
      className={cn(
        'rounded-2xl transition-all duration-200',
        paddings[padding],
        className
      )}
      style={getVariantStyles()}
      {...props}
    >
      {children}
    </div>
  );
});
GlassCard.displayName = 'GlassCard';

// ============================================
// STATUS CHIP - USES SEMANTIC TOKENS
// ============================================

export const StatusChip = ({ 
  status = 'neutral', 
  children, 
  icon,
  className,
  ...props 
}) => {
  const statusConfig = {
    success: {
      className: 'chip-semantic-success',
      icon: <CheckCircle className="w-3.5 h-3.5" />
    },
    warning: {
      className: 'chip-semantic-warning',
      icon: <AlertCircle className="w-3.5 h-3.5" />
    },
    error: {
      className: 'chip-semantic-danger',
      icon: <XCircle className="w-3.5 h-3.5" />
    },
    pending: {
      className: 'chip-semantic-info',
      icon: <Clock className="w-3.5 h-3.5" />
    },
    neutral: {
      className: 'bg-semantic-surface-2 text-semantic-2',
      icon: null
    }
  };
  
  const config = statusConfig[status] || statusConfig.neutral;
  
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium',
        config.className,
        className
      )}
      {...props}
    >
      {icon || config.icon}
      {children}
    </span>
  );
};

// ============================================
// PILL BUTTON - USES SEMANTIC TOKENS
// ============================================

export const PillButton = React.forwardRef(({ 
  className, 
  variant = 'primary',
  size = 'default',
  icon,
  iconPosition = 'left',
  loading = false,
  disabled = false,
  children, 
  ...props 
}, ref) => {
  // Use inline styles for dark mode compatibility
  const getVariantStyles = () => {
    switch (variant) {
      case 'primary':
        return {
          backgroundColor: 'hsl(var(--color-primary))',
          color: 'hsl(var(--color-primary-text))',
          boxShadow: '0 4px 14px hsl(var(--color-primary) / 0.25)'
        };
      case 'secondary':
        return {
          backgroundColor: 'hsl(var(--color-surface-2))',
          color: 'hsl(var(--color-text))'
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: 'hsl(var(--color-text))',
          border: '2px solid hsl(var(--color-border))'
        };
      case 'ghost':
        return {
          backgroundColor: 'transparent',
          color: 'hsl(var(--color-text-2))'
        };
      case 'success':
        return {
          backgroundColor: 'hsl(var(--color-success))',
          color: 'white',
          boxShadow: '0 4px 14px hsl(var(--color-success) / 0.25)'
        };
      case 'danger':
      case 'destructive':
        return {
          backgroundColor: 'hsl(var(--color-destructive, 0 72% 51%))',
          color: 'white',
          boxShadow: '0 4px 14px hsl(var(--color-destructive, 0 72% 51%) / 0.25)'
        };
      default:
        return {};
    }
  };
  
  const sizes = {
    sm: 'px-4 py-2 text-sm',
    default: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg'
  };
  
  return (
    <button
      ref={ref}
      type="button"  // P0 FIX: Always button type to prevent form submission (Safari iOS 405 bug)
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center gap-2 font-semibold rounded-full',
        'transition-all duration-200',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none',
        'active:scale-[0.98]',
        sizes[size],
        className
      )}
      style={{
        ...getVariantStyles(),
        '--tw-ring-color': 'hsl(var(--color-focus))'
      }}
      {...props}
    >
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {!loading && icon && iconPosition === 'left' && icon}
      {children}
      {!loading && icon && iconPosition === 'right' && icon}
    </button>
  );
});
PillButton.displayName = 'PillButton';

// ============================================
// SIDEBAR STEP ITEM - USES SEMANTIC TOKENS
// ============================================

export const SidebarStepItem = ({ 
  step,
  title,
  icon,
  status = 'pending', // pending, active, completed, warning, error
  locked = false,
  hasMissingPrereqs = false, // P0 UX OVERHAUL v2: Show warning badge
  onClick,
  className,
  ...props 
}) => {
  const getStatusStyles = () => {
    switch (status) {
      case 'completed':
        return {
          iconBg: 'hsl(var(--color-success))',
          iconText: 'white',
          textColor: 'hsl(var(--color-text))',
          indicator: <CheckCircle className="w-4 h-4" style={{ color: 'hsl(var(--color-success))' }} />
        };
      case 'active':
        return {
          iconBg: 'hsl(var(--color-primary))',
          iconText: 'white',
          textColor: 'hsl(var(--sidebar-text-active))',
          indicator: null
        };
      case 'warning':
        return {
          iconBg: 'hsl(var(--color-warning))',
          iconText: 'white',
          textColor: 'hsl(var(--color-text))',
          indicator: <AlertCircle className="w-4 h-4" style={{ color: 'hsl(var(--color-warning))' }} />
        };
      case 'error':
        return {
          iconBg: 'hsl(var(--color-danger))',
          iconText: 'white',
          textColor: 'hsl(var(--color-text))',
          indicator: <XCircle className="w-4 h-4" style={{ color: 'hsl(var(--color-danger))' }} />
        };
      default: // pending
        return {
          iconBg: 'hsl(var(--color-surface-2))',
          iconText: 'hsl(var(--color-text-muted))',
          textColor: locked ? 'hsl(var(--sidebar-text-disabled))' : 'hsl(var(--sidebar-text))',
          indicator: <Clock className="w-3 h-3" style={{ color: 'hsl(var(--color-text-muted))' }} />
        };
    }
  };
  
  const styles = getStatusStyles();
  
  return (
    <button
      onClick={onClick}
      disabled={locked}
      className={cn(
        'w-full flex items-center gap-3 px-4 py-3 rounded-xl',
        'transition-all duration-200',
        status === 'active' && 'font-semibold',
        locked && 'opacity-50 cursor-not-allowed',
        className
      )}
      style={{
        backgroundColor: status === 'active' ? 'hsl(var(--sidebar-item-active-bg))' : 'transparent'
      }}
      {...props}
    >
      {/* Step Icon */}
      <div 
        className="w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold relative"
        style={{
          backgroundColor: styles.iconBg,
          color: styles.iconText
        }}
      >
        {icon || step}
        {/* P0 UX OVERHAUL v2: Missing prerequisites badge */}
        {hasMissingPrereqs && status !== 'completed' && (
          <span 
            className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center rounded-full text-xs font-bold"
            style={{ 
              backgroundColor: 'hsl(var(--color-warning))',
              color: 'white'
            }}
            title="Prérequis manquants"
          >
            ⚠
          </span>
        )}
      </div>
      
      {/* Title */}
      <span 
        className="flex-1 text-left"
        style={{ color: styles.textColor }}
      >
        {title}
      </span>
      
      {/* Status Indicator */}
      {styles.indicator && (
        <div className="flex-shrink-0">
          {styles.indicator}
        </div>
      )}
      
      {/* Chevron for active */}
      {status === 'active' && (
        <ChevronRight className="w-4 h-4" style={{ color: 'hsl(var(--sidebar-text-active))' }} />
      )}
    </button>
  );
};

// ============================================
// PLATFORM CARD
// ============================================

export const PlatformCard = ({
  icon,
  name,
  description,
  status = 'not_connected', // not_connected, connecting, connected, error
  onConfigure,
  onDiagnose,
  className,
  ...props
}) => {
  const statusConfig = {
    not_connected: {
      chip: <StatusChip status="error">Non connecté</StatusChip>,
      chipIcon: '❌'
    },
    connecting: {
      chip: <StatusChip status="pending">Connexion...</StatusChip>,
      chipIcon: '⏳'
    },
    connected: {
      chip: <StatusChip status="success">Connecté</StatusChip>,
      chipIcon: '✅'
    },
    error: {
      chip: <StatusChip status="warning">À vérifier</StatusChip>,
      chipIcon: '⚠️'
    }
  };
  
  const config = statusConfig[status] || statusConfig.not_connected;
  
  return (
    <div
      className={cn(
        'flex items-center justify-between p-5',
        'bg-white border border-gray-200 rounded-2xl',
        'transition-all duration-200',
        'hover:border-blue-200 hover:shadow-md',
        className
      )}
      {...props}
    >
      {/* Left: Icon + Info */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gray-100 text-2xl">
          {icon}
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">{name}</h3>
          {description && (
            <p className="text-sm text-gray-500 mt-0.5">{description}</p>
          )}
        </div>
      </div>
      
      {/* Right: Status + Actions */}
      <div className="flex items-center gap-3">
        {config.chip}
        
        <div className="flex gap-2">
          <PillButton 
            variant="primary" 
            size="sm" 
            onClick={onConfigure}
          >
            Configurer
          </PillButton>
          {status !== 'connected' && (
            <PillButton 
              variant="ghost" 
              size="sm" 
              onClick={onDiagnose}
            >
              Diagnostiquer
            </PillButton>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// SECTION HEADER
// ============================================

export const SectionHeader = ({ 
  title, 
  subtitle, 
  action,
  className,
  ...props 
}) => (
  <div className={cn('mb-6', className)} {...props}>
    <div className="flex items-start justify-between">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
        {subtitle && (
          <p className="text-gray-500 mt-1">{subtitle}</p>
        )}
      </div>
      {action && <div>{action}</div>}
    </div>
  </div>
);

// ============================================
// PROGRESS INDICATOR
// ============================================

export const ProgressIndicator = ({ 
  steps = [], 
  currentStep = 0,
  className,
  ...props 
}) => (
  <div className={cn('flex items-center gap-2', className)} {...props}>
    {steps.map((step, index) => (
      <React.Fragment key={index}>
        <div
          className={cn(
            'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold',
            'transition-all duration-200',
            index < currentStep && 'bg-emerald-500 text-white',
            index === currentStep && 'bg-blue-500 text-white',
            index > currentStep && 'bg-gray-200 text-gray-500'
          )}
        >
          {index < currentStep ? (
            <CheckCircle className="w-4 h-4" />
          ) : (
            index + 1
          )}
        </div>
        {index < steps.length - 1 && (
          <div 
            className={cn(
              'flex-1 h-1 rounded-full',
              index < currentStep ? 'bg-emerald-500' : 'bg-gray-200'
            )}
          />
        )}
      </React.Fragment>
    ))}
  </div>
);

// ============================================
// EMPTY STATE
// ============================================

export const EmptyState = ({
  icon,
  title,
  description,
  action,
  className,
  ...props
}) => (
  <div 
    className={cn(
      'flex flex-col items-center justify-center py-16 px-8 text-center',
      className
    )}
    {...props}
  >
    {icon && (
      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
        {icon}
      </div>
    )}
    <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
    {description && (
      <p className="text-gray-500 max-w-sm mb-6">{description}</p>
    )}
    {action}
  </div>
);

// ============================================
// LOADING SPINNER
// ============================================

export const LoadingSpinner = ({ 
  size = 'default',
  className,
  ...props 
}) => {
  const sizes = {
    sm: 'w-4 h-4',
    default: 'w-6 h-6',
    lg: 'w-8 h-8'
  };
  
  return (
    <Loader2 
      className={cn('animate-spin text-blue-500', sizes[size], className)} 
      {...props}
    />
  );
};

export default {
  GlassCard,
  StatusChip,
  PillButton,
  SidebarStepItem,
  PlatformCard,
  SectionHeader,
  ProgressIndicator,
  EmptyState,
  LoadingSpinner
};
